import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
    LayoutDashboard, Key, Laptop, Cloud, Lock, Shield, Users, Database,
    Activity, GitBranch, DollarSign, AlertTriangle, Settings, LogOut, Rocket,
    MessageSquare, CheckSquare, Zap, BookOpen, Inbox, ChevronDown, ChevronRight
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { isAhaComplete, subscribe } from '../../data/demoStore';

const fullNavItems = [
    {
        title: "Overview",
        items: [
            { label: 'Control Tower', path: '/control-tower', icon: LayoutDashboard },
            { label: 'Cases', path: '/tickets', icon: MessageSquare },
            { label: 'Approvals', path: '/approvals', icon: CheckSquare },
            { label: 'Setup', path: '/setup', icon: Rocket },
        ]
    },
    {
        title: "Domains",
        items: [
            { label: 'Identity', path: '/identity', icon: Key },
            { label: 'Endpoints', path: '/endpoints', icon: Laptop },
            { label: 'SaaS', path: '/saas', icon: Cloud },
            { label: 'Security', path: '/security', icon: Lock },
        ]
    },
    {
        title: "Tools",
        items: [
            { label: 'Workflows', path: '/workflow-library', icon: Zap },
            { label: 'Guidance', path: '/guidance', icon: BookOpen },
            { label: 'Knowledge', path: '/knowledge', icon: Database },
            { label: 'Suggestions', path: '/suggestions', icon: Activity },
            { label: 'Policy Studio', path: '/policy-studio', icon: Shield },
            { label: 'Agent Fleet', path: '/agents', icon: Users },
        ]
    },
    {
        title: "Evidence",
        items: [
            { label: 'Asset Graph', path: '/assets', icon: Database },
            { label: 'Config Intelligence', path: '/services', icon: GitBranch },
            { label: 'Reconciliation', path: '/reconciliation', icon: AlertTriangle },
            { label: 'Cost Attribution', path: '/cost', icon: DollarSign },
        ]
    },
    {
        title: "System",
        items: [
            { label: 'Audit Log', path: '/audit', icon: Activity },
        ]
    },
    {
        title: "Settings",
        items: [
            { label: 'Teams', path: '/settings/teams', icon: Users },
            { label: 'People', path: '/settings/people', icon: Users },
            { label: 'Channels', path: '/settings/channels', icon: MessageSquare },
            { label: 'Routing', path: '/settings/routing', icon: GitBranch },
        ]
    }
];

const minimalNavItems = [
    {
        title: "Help Desk",
        items: [
            { label: 'Portal', path: '/portal', icon: MessageSquare },
            { label: 'Cases', path: '/tickets', icon: Inbox },
            { label: 'Approvals', path: '/approvals', icon: CheckSquare },
        ]
    },
    {
        title: "Me",
        items: [
            { label: 'My Requests', path: '/my-requests', icon: MessageSquare },
        ]
    },
];

function useAdvancedNav() {
    const [aha, setAha] = useState(isAhaComplete());
    const [showAdvanced, setShowAdvanced] = useState(() => {
        try {
            return localStorage.getItem('vedaya_show_advanced_nav') === 'true';
        } catch {
            return false;
        }
    });

    useEffect(() => {
        return subscribe(() => {
            setAha(isAhaComplete());
            try {
                setShowAdvanced(localStorage.getItem('vedaya_show_advanced_nav') === 'true');
            } catch {
                setShowAdvanced(false);
            }
        });
    }, []);

    const toggleAdvanced = () => {
        const next = !showAdvanced;
        try {
            localStorage.setItem('vedaya_show_advanced_nav', next ? 'true' : 'false');
        } catch {}
        setShowAdvanced(next);
        window.dispatchEvent(new Event('vedaya-demo-store'));
    };

    return { aha, showAdvanced: aha && showAdvanced, toggleAdvanced };
}

export function Sidebar({ className }) {
    const { aha, showAdvanced, toggleAdvanced } = useAdvancedNav();

    const navGroups = showAdvanced ? fullNavItems : minimalNavItems;

    return (
        <aside className={cn("flex flex-col border-r bg-sidebar text-sidebar-foreground w-64", className)} aria-label="Main navigation">
            <div className="flex h-14 items-center border-b border-sidebar-border px-6">
                <NavLink to="/" className="flex items-center gap-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded bg-sidebar-primary text-sidebar-primary-foreground font-bold">
                        V
                    </div>
                    <span className="font-semibold tracking-tight text-sidebar-foreground">Vedaya</span>
                </NavLink>
            </div>

            <div className="flex-1 overflow-y-auto py-4">
                <nav className="space-y-6 px-3" role="navigation" aria-label="Primary">
                    {navGroups.map((group, i) => (
                        <div key={i}>
                            <h3 className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/50">
                                {group.title}
                            </h3>
                            <div className="space-y-0.5">
                                {group.items.map((item) => (
                                    <SidebarItem key={item.path} item={item} />
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>
            </div>

            <div className="border-t border-sidebar-border p-4" role="group" aria-label="User actions">
                {aha && (
                    <>
                        <button
                            onClick={toggleAdvanced}
                            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                            aria-label="Toggle advanced navigation"
                        >
                            {showAdvanced ? (
                                <ChevronDown className="h-4 w-4" aria-hidden="true" />
                            ) : (
                                <ChevronRight className="h-4 w-4" aria-hidden="true" />
                            )}
                            <span>{showAdvanced ? 'Hide Advanced' : 'Show Advanced'}</span>
                        </button>
                        <NavLink
                            to="/setup"
                            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                            aria-label="Open settings"
                        >
                            <Settings className="h-4 w-4" aria-hidden="true" />
                            <span>Settings</span>
                        </NavLink>
                        <button
                            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors mt-1"
                            aria-label="Sign out of account"
                        >
                            <LogOut className="h-4 w-4" aria-hidden="true" />
                            <span>Sign Out</span>
                        </button>
                    </>
                )}
            </div>
        </aside>
    );
}

function SidebarItem({ item }) {
    return (
        <NavLink
            to={item.path}
            className={({ isActive }) => cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive
                    ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-sm"
                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
            )}
        >
            <item.icon className="h-4 w-4 opacity-80" aria-hidden="true" />
            <span>{item.label}</span>
        </NavLink>
    );
}
